import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Shield, Zap, AlertTriangle, CheckCircle, Info, XCircle, Search, X, Filter, FileText } from 'lucide-react';
import { QuoteRequestDialog } from './QuoteRequestDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Component to display all services for a subcategory
export default function SubcategoryServicesDisplay({ 
  subcategorySlug, 
  country = 'UK',
  showInAccordion = false,
}) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'emergency', 'regular'

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/trades-services/config/subcategory/${subcategorySlug}`);
        if (res.ok) {
          const data = await res.json();
          setConfig(data.subcategory);
        }
      } catch (error) {
        console.error('Failed to fetch subcategory config:', error);
      } finally {
        setLoading(false);
      }
    };

    if (subcategorySlug) {
      fetchConfig();
    }
  }, [subcategorySlug]);

  // Filter services based on search and filter type
  const filteredServices = useMemo(() => {
    if (!config?.services) return [];
    
    let services = Object.entries(config.services);
    
    // Apply filter type
    if (filterType === 'emergency') {
      services = services.filter(([_, s]) => s.allow_emergency);
    } else if (filterType === 'regular') {
      services = services.filter(([_, s]) => !s.allow_emergency);
    }
    
    // Apply search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      services = services.filter(([key, s]) => 
        s.name?.toLowerCase().includes(term) ||
        s.description?.toLowerCase().includes(term) ||
        key.toLowerCase().includes(term)
      );
    }
    
    return services;
  }, [config, searchTerm, filterType]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (!config) return null;

  const allServices = Object.entries(config.services || {});
  const emergencyCount = allServices.filter(([_, s]) => s.allow_emergency).length;
  const regularCount = allServices.length - emergencyCount;

  const getComplianceBadge = (level) => {
    const colors = {
      low: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-red-100 text-red-700',
    };
    return (
      <Badge className={colors[level] || colors.low}>
        {level?.toUpperCase() || 'LOW'} Compliance
      </Badge>
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterType('all');
  };

  if (showInAccordion) {
    return (
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="services">
          <AccordionTrigger className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-xl">{config.subcategory_icon}</span>
              <span className="font-semibold">{config.subcategory_name}</span>
              <Badge variant="outline">{allServices.length} services</Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <ServicesGridWithSearch 
              services={filteredServices}
              allServices={allServices}
              config={config}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterType={filterType}
              setFilterType={setFilterType}
              emergencyCount={emergencyCount}
              regularCount={regularCount}
              clearFilters={clearFilters}
              subcategorySlug={subcategorySlug}
              country={country}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">{config.subcategory_icon}</span>
            {config.subcategory_name}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {getComplianceBadge(config.compliance_level)}
            {config.licensed_trade && (
              <Badge className="bg-purple-100 text-purple-700">
                <Shield className="w-3 h-3 mr-1" />
                Licensed Trade
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ServicesGridWithSearch 
          services={filteredServices}
          allServices={allServices}
          config={config}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterType={filterType}
          setFilterType={setFilterType}
          emergencyCount={emergencyCount}
          regularCount={regularCount}
          clearFilters={clearFilters}
          subcategorySlug={subcategorySlug}
          country={country}
        />
      </CardContent>
    </Card>
  );
}

// Services Grid with Search/Filter Component
function ServicesGridWithSearch({ 
  services, 
  allServices,
  config, 
  searchTerm, 
  setSearchTerm, 
  filterType, 
  setFilterType,
  emergencyCount,
  regularCount,
  clearFilters,
  subcategorySlug,
  country,
}) {
  const hasFilters = searchTerm || filterType !== 'all';

  return (
    <div className="space-y-4">
      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search services... (e.g., plumbing, cleaning, repair)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Filter Buttons */}
        <div className="flex gap-2">
          <Button
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('all')}
            className="text-xs"
          >
            All ({allServices.length})
          </Button>
          {emergencyCount > 0 && (
            <Button
              variant={filterType === 'emergency' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('emergency')}
              className={`text-xs ${filterType === 'emergency' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-200 hover:bg-red-50'}`}
            >
              <Zap className="w-3 h-3 mr-1" />
              Emergency ({emergencyCount})
            </Button>
          )}
          <Button
            variant={filterType === 'regular' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('regular')}
            className="text-xs"
          >
            Regular ({regularCount})
          </Button>
        </div>
      </div>

      {/* Active Filters Indicator */}
      {hasFilters && (
        <div className="flex items-center gap-2 text-sm">
          <Filter className="w-4 h-4 text-blue-500" />
          <span className="text-gray-600">
            Showing {services.length} of {allServices.length} services
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-xs text-blue-600 hover:text-blue-800 h-auto p-1"
          >
            Clear filters
          </Button>
        </div>
      )}

      {/* No Results */}
      {services.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <Search className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">No services found matching "{searchTerm}"</p>
          <Button
            variant="link"
            onClick={clearFilters}
            className="text-blue-600 mt-2"
          >
            Clear filters to see all services
          </Button>
        </div>
      )}

      {/* Admin Rules & Notes */}
      {config.admin_rules && Object.keys(config.admin_rules).length > 0 && !hasFilters && (
        <AdminRulesSection config={config} />
      )}

      {/* Explicitly Excluded */}
      {config.explicitly_excluded && config.explicitly_excluded.length > 0 && !hasFilters && (
        <ExcludedSection excluded={config.explicitly_excluded} />
      )}

      {/* Services Grid */}
      {services.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            {filterType === 'emergency' && <Zap className="w-4 h-4 text-red-500" />}
            {filterType === 'emergency' ? 'Emergency Services' : filterType === 'regular' ? 'Regular Services' : 'All Services'} 
            ({services.length})
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {services.map(([key, service]) => (
              <ServiceCard 
                key={key} 
                service={{...service, key}} 
                isEmergency={service.allow_emergency} 
                searchTerm={searchTerm}
                subcategory={subcategorySlug}
                country={country}
              />
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {!hasFilters && (
        <>
          {config.high_repeat_revenue && (
            <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs text-green-700 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                High repeat & subscription revenue potential
              </p>
            </div>
          )}
          {config.clean_category && (
            <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Clean category - No licensing or compliance risk
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Admin Rules Section
function AdminRulesSection({ config }) {
  return (
    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
      <p className="text-sm font-medium text-amber-700 flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4" />
        Admin Rules & Compliance
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        {config.admin_rules.block_claims && (
          <div className="flex items-start gap-1 text-red-600">
            <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>Block: {config.admin_rules.block_claims.join(', ')}</span>
          </div>
        )}
        {config.admin_rules.allow_licence_claims && (
          <div className="flex items-start gap-1 text-green-600">
            <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>Allow licence claims</span>
          </div>
        )}
        {config.admin_rules.verify_licences === false && (
          <div className="flex items-start gap-1 text-amber-600">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>Licences NOT verified</span>
          </div>
        )}
        {config.admin_rules.country_disclaimers_required && (
          <div className="flex items-start gap-1 text-blue-600">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>Country disclaimers required</span>
          </div>
        )}
        {config.admin_rules.business_verification_required && (
          <div className="flex items-start gap-1 text-purple-600">
            <Shield className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>Business verification required</span>
          </div>
        )}
        {config.admin_rules.flag_language && (
          <div className="flex items-start gap-1 text-orange-600">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>Flag: {config.admin_rules.flag_language.join(', ')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Excluded Section
function ExcludedSection({ excluded }) {
  return (
    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
      <p className="text-sm font-medium text-red-700 flex items-center gap-2 mb-2">
        <XCircle className="w-4 h-4" />
        Explicitly Excluded (Not Allowed)
      </p>
      <div className="flex flex-wrap gap-2">
        {excluded.map((item, idx) => (
          <Badge key={idx} variant="outline" className="border-red-300 text-red-700">
            ❌ {item}
          </Badge>
        ))}
      </div>
    </div>
  );
}

// Individual Service Card with search highlight and quote button
function ServiceCard({ service, isEmergency, searchTerm = '', subcategory, country = 'UK' }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Function to highlight matching text
  const highlightMatch = (text) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => 
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 px-0.5 rounded">{part}</mark>
      ) : part
    );
  };

  return (
    <>
      <div
        className={`p-2 rounded-lg border text-sm transition-all hover:shadow-md cursor-pointer group ${
          isEmergency
            ? 'bg-red-50 border-red-200 hover:border-red-300'
            : 'bg-gray-50 border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => setDialogOpen(true)}
      >
        <div className="flex items-start gap-2">
          <span className="text-base flex-shrink-0">{service.icon}</span>
          <div className="flex-1 min-w-0">
            <span className="font-medium block truncate">{highlightMatch(service.name)}</span>
            {service.description && (
              <span className="text-xs text-gray-500 block truncate">{highlightMatch(service.description)}</span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between mt-2">
          {isEmergency && (
            <Badge className="bg-red-100 text-red-700 text-xs">
              🆘 Emergency
            </Badge>
          )}
          <Button 
            size="sm" 
            variant="outline" 
            className="text-xs h-6 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              setDialogOpen(true);
            }}
          >
            <FileText className="w-3 h-3 mr-1" />
            Quote
          </Button>
        </div>
      </div>
      
      <QuoteRequestDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        service={service}
        subcategory={subcategory}
        country={country}
      />
    </>
  );
}

// Component to show all subcategories with their services
export function AllSubcategoriesServicesDisplay({ country = 'UK' }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/trades-services/config/country/${country}`);
        if (res.ok) {
          const data = await res.json();
          setConfig(data);
        }
      } catch (error) {
        console.error('Failed to fetch country config:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [country]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">
          Services & Trades - {config.country?.name}
        </h2>
        <Badge variant="outline">
          {config.subcategories?.length || 0} categories
        </Badge>
      </div>

      <Tabs defaultValue={config.subcategories?.[0]?.slug}>
        <TabsList className="flex flex-wrap h-auto gap-1 mb-4">
          {config.subcategories?.map((subcat) => (
            <TabsTrigger 
              key={subcat.slug} 
              value={subcat.slug}
              className="text-xs px-2 py-1"
            >
              {subcat.subcategory_icon} {subcat.subcategory_name}
              {subcat.is_custom && (
                <Badge className="ml-1 bg-blue-100 text-blue-700 text-[10px] px-1">
                  {subcat.country_only}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {config.subcategories?.map((subcat) => (
          <TabsContent key={subcat.slug} value={subcat.slug}>
            <SubcategoryServicesDisplayInline subcat={subcat} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

// Inline subcategory display (using pre-fetched data)
function SubcategoryServicesDisplayInline({ subcat }) {
  const services = Object.entries(subcat.services || {});
  const emergencyServices = services.filter(([_, s]) => s.allow_emergency);

  const getComplianceBadge = (level) => {
    const colors = {
      low: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-red-100 text-red-700',
    };
    return (
      <Badge className={colors[level] || colors.low}>
        {level?.toUpperCase() || 'LOW'} Compliance
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">{subcat.subcategory_icon}</span>
            {subcat.subcategory_name}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {getComplianceBadge(subcat.compliance_level)}
            {subcat.licensed_trade && (
              <Badge className="bg-purple-100 text-purple-700">
                <Shield className="w-3 h-3 mr-1" />
                Licensed Trade
              </Badge>
            )}
            {subcat.is_custom && (
              <Badge className="bg-blue-100 text-blue-700">
                {subcat.country_only}-only
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Disclaimer */}
        {subcat.disclaimer && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {subcat.disclaimer}
            </p>
          </div>
        )}

        {/* Emergency Services */}
        {emergencyServices.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-medium text-red-700 flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4" />
              Emergency Services ({emergencyServices.length})
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {emergencyServices.map(([key, service]) => (
                <ServiceCard key={key} service={service} isEmergency={true} />
              ))}
            </div>
          </div>
        )}

        {/* All Services */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            All Services ({services.length} total)
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {services.map(([key, service]) => (
              <ServiceCard key={key} service={service} isEmergency={service.allow_emergency} />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
